import { Inject, Injectable } from '@angular/core';
import {
    ConfigService,
    FileStorageInjectionToken,
    FileStorageServiceInterface,
    NotificationIdentifier,
} from '@evorait/pace-core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { FormState } from '../store/state/form.state';
import { LastOpenedFormState } from '../store/state/last-opened-form.state';
import { FormIdentifier } from '../types/form-identifier';
import { FormSubmitStatus } from '../types/form-submit-status';
import { FormsConstants } from '../types/forms-constants';
import { ODataSPQModel } from '../types/service-part-questionnaire';
import { FormUtilities } from '../utilities/form-utilities';
import { NotificationUtilities } from '../utilities/notification-utilities';
import { ODataEquipmentSet, ODataGetListModel, ODataGetXmlModel, ODataPrologueModel } from './forms-odata.interface';
import { ODataGetListModelWithOldEntries } from './forms-read.service';
import SUBMIT_STATUS = FormsConstants.SUBMIT_STATUS;
import isServicePartQuestionnaire = FormUtilities.isServicePartQuestionnaire;

@Injectable({
    providedIn: 'root',
})
export class FormsStorageService {
    private readonly fileNameSeparator: string;
    public constructor(
        @Inject(FileStorageInjectionToken) private readonly fileStorage: FileStorageServiceInterface,
        private readonly configService: ConfigService,
    ) {
        this.fileNameSeparator = '-';
    }

    public saveFormSubmitStatus(
        notification: NotificationIdentifier,
        formId: FormIdentifier,
        status: FormsConstants.SUBMIT_STATUS,
        materialKey = '',
    ): Observable<FormSubmitStatus> {
        if (
            FormUtilities.isServicePartQuestionnaire(FormUtilities.getFormTypeFromId(formId.formId)) &&
            (status === SUBMIT_STATUS.FINAL || status === SUBMIT_STATUS.FINAL_NOT_OK)
        ) {
            return this.saveSPQSubmitStatus(notification, formId, materialKey, status);
        }

        const safetyFormId = this.configService.getSafetyAssessmentForm();

        if (FormUtilities.isSafetyAssessmentForm(formId.formId, safetyFormId)) {
            return this.saveSAFSubmitStatus(notification, formId, status);
        }

        const notificationString = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormSubmitStatus${this.fileNameSeparator}${notificationString}`;
        return this.loadFormSubmitStatus(notification).pipe(
            mergeMap((savedStatus) =>
                this.fileStorage.storeCompressedData(storageId, {
                    ...savedStatus,
                    [formId.formId]: status,
                }),
            ),
        );
    }

    public loadFormSubmitStatus(notification: NotificationIdentifier): Observable<FormSubmitStatus> {
        const id = NotificationUtilities.toString(notification);

        const storageId = `${FormsConstants.KEY_FORMS}.FormSubmitStatus${this.fileNameSeparator}${id}`;

        return this.fileStorage.loadCompressedData<FormSubmitStatus>(storageId).pipe(catchError(() => of({})));
    }

    public removeFormSubmitStatus(notification: NotificationIdentifier): Observable<void> {
        const id = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormSubmitStatus${this.fileNameSeparator}${id}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public saveSPQSubmitStatus(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        materialKey: string,
        submitStatus: FormsConstants.SUBMIT_STATUS,
    ): Observable<FormSubmitStatus> {
        const storageId = this.makeSPQSubmitStatusStorageId(notification, form, materialKey);
        return this.fileStorage.storeCompressedData(storageId, submitStatus).pipe(
            map(() => {
                return {
                    [form.formId]: submitStatus,
                };
            }),
        );
    }

    public loadSPQSubmitStatus(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        materialKey: string,
    ): Observable<SUBMIT_STATUS> {
        const storageId = this.makeSPQSubmitStatusStorageId(notification, form, materialKey);
        return this.fileStorage.loadCompressedData<SUBMIT_STATUS>(storageId);
    }

    public removeSPQSubmitStatus(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        materialKey: string,
    ): Observable<void> {
        const storageId = this.makeSPQSubmitStatusStorageId(notification, form, materialKey);

        return this.fileStorage.removeCompressedData(storageId);
    }

    // SafetyAssessmentForm
    public saveSAFSubmitStatus(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        submitStatus: FormsConstants.SUBMIT_STATUS,
    ): Observable<FormSubmitStatus> {
        const storageId = this.makeSAFSubmitStatusStorageId(notification, form);
        return this.fileStorage.storeCompressedData(storageId, submitStatus).pipe(
            map(() => {
                return {
                    [form.formId]: submitStatus,
                };
            }),
        );
    }
    public removeSAFSubmitStatus(notification: NotificationIdentifier, form: FormIdentifier): Observable<void> {
        const storageId = this.makeSAFSubmitStatusStorageId(notification, form);

        return this.fileStorage.removeCompressedData(storageId);
    }

    public saveSAFXmlToCache(
        formId: FormIdentifier,
        notification: NotificationIdentifier,
        formXml: ODataGetXmlModel,
    ): Observable<ODataGetXmlModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.SAFFormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.storeCompressedData(storageId, formXml);
    }

    public isSAFDataInCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<boolean> {
        const storageId = this.makeSAFSubmitStatusStorageId(notification, formId);

        return this.fileStorage.checkCompressedData(storageId);
    }

    public removeSAFXmlFromCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<void> {
        const storageId = `${FormsConstants.KEY_FORMS}.SAFFormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public removeFormSubmitStatusForForm(
        form: FormIdentifier,
        notification: NotificationIdentifier,
    ): Observable<FormSubmitStatus> {
        const notificationString = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormSubmitStatus${this.fileNameSeparator}${notificationString}`;
        return this.loadFormSubmitStatus(notification).pipe(
            mergeMap((savedStatus) => {
                const cleanedSavedStatus = { ...savedStatus };
                delete cleanedSavedStatus[form.formId];
                return this.fileStorage.storeCompressedData(storageId, cleanedSavedStatus);
            }),
        );
    }

    public saveFormListOdataToCache(
        materialId: string,
        equipmentId: string,
        notification: NotificationIdentifier,
        formListXml: ODataGetListModelWithOldEntries[],
    ): Observable<ODataGetListModelWithOldEntries[]> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormListOdata${this.fileNameSeparator}${materialId}${this.fileNameSeparator}${equipmentId}_${notificationId}`;
        return this.fileStorage.storeCompressedData(storageId, formListXml);
    }

    public loadFormListOdataFromCache(
        materialId: string,
        equipmentId: string,
        notification: NotificationIdentifier,
    ): Observable<ODataGetListModelWithOldEntries[] | undefined> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormListOdata${this.fileNameSeparator}${materialId}${this.fileNameSeparator}${equipmentId}_${notificationId}`;

        return this.fileStorage
            .loadCompressedData<ODataGetListModelWithOldEntries[]>(storageId)
            .pipe(catchError(() => of(undefined)));
    }

    public saveEquipmentSetODataToCache(
        notification: NotificationIdentifier,
        data: ODataEquipmentSet[],
    ): Observable<ODataEquipmentSet[]> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.EquipmentSetOdata${this.fileNameSeparator}${notificationId}`;

        return this.fileStorage.storeCompressedData(storageId, data);
    }

    public loadEquipmentSetOdataFromCache(
        notification: NotificationIdentifier,
    ): Observable<ODataEquipmentSet[] | undefined> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.EquipmentSetOdata${this.fileNameSeparator}${notificationId}`;

        return this.fileStorage
            .loadCompressedData<ODataEquipmentSet[]>(storageId)
            .pipe(catchError(() => of(undefined)));
    }

    public removeEquipmentSetOdataFromCache(notification: NotificationIdentifier): Observable<void> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.EquipmentSetOdata${this.fileNameSeparator}${notificationId}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public savePrologueOdataToCache(
        type: FormsConstants.FORMTYPE,
        language: string,
        prologue: ODataPrologueModel,
    ): Observable<ODataPrologueModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.PrologueOdata${this.fileNameSeparator}${type}${this.fileNameSeparator}${language}`;
        return this.fileStorage.storeCompressedData(storageId, prologue);
    }

    public loadPrologueOdataFromCache(
        type: FormsConstants.FORMTYPE,
        language: string,
    ): Observable<ODataPrologueModel | undefined> {
        const storageId = `${FormsConstants.KEY_FORMS}.PrologueOdata${this.fileNameSeparator}${type}${this.fileNameSeparator}${language}`;

        return this.fileStorage.loadCompressedData<ODataPrologueModel>(storageId).pipe(catchError(() => of(undefined)));
    }

    public removeFormListOdataCache(
        materialId: string,
        equipmentId: string,
        notification: NotificationIdentifier,
    ): Observable<void> {
        const notificationId = NotificationUtilities.toString(notification);
        const storageId = `${FormsConstants.KEY_FORMS}.FormListOdata${this.fileNameSeparator}${materialId}${this.fileNameSeparator}${equipmentId}_${notificationId}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public removeLockFromCachedOdataList(
        form: FormIdentifier,
        notificationId: NotificationIdentifier,
    ): Observable<ODataGetListModel[]> {
        // Load list, identify entry for form and set "LockedBy" to an empty string
        return this.loadFormListOdataFromCache(form.materialId, form.equipmentId, notificationId).pipe(
            mergeMap((formListOdata) => {
                if (typeof formListOdata === 'undefined' || formListOdata.length !== 1) {
                    return throwError(
                        new Error(
                            `FormsStorageService: removeLockFromCachedList: Could not load form list for equipment '${
                                form.equipmentId
                            }', material '${form.materialId}', notification '${NotificationUtilities.toString(
                                notificationId,
                            )}'`,
                        ),
                    );
                }

                const entry = formListOdata[0].FormID_Navigation.results.find((e) => e.FormDocid === form.formId);
                if (typeof entry === 'undefined') {
                    return throwError(
                        new Error(
                            `FormsStorageService: removeLockFromCachedList: Could not find entry for form '${
                                form.formId
                            }' in form list for equipment '${form.equipmentId}', material '${
                                form.materialId
                            }', notification '${NotificationUtilities.toString(notificationId)}'`,
                        ),
                    );
                }
                entry.LockedBy = '';

                return this.saveFormListOdataToCache(form.materialId, form.equipmentId, notificationId, formListOdata);
            }),
        );
    }

    public saveFormXmlToCache(
        formId: FormIdentifier,
        notification: NotificationIdentifier,
        formXml: ODataGetXmlModel,
    ): Observable<ODataGetXmlModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.storeCompressedData(storageId, formXml);
    }

    public loadFormXmlFromCache(
        id: FormIdentifier,
        notification: NotificationIdentifier,
    ): Observable<ODataGetXmlModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormXml${this.fileNameSeparator}${this.makeId(
            id,
            notification,
        )}`;

        return this.fileStorage.loadCompressedData<ODataGetXmlModel>(storageId);
    }

    public isFormXmlInCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<boolean> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.checkCompressedData(storageId);
    }

    public removeFormXmlFromCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<void> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public saveQuestionnaireXmlToCache(docId: string, docXml: ODataSPQModel): Observable<ODataSPQModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.SPQXml${this.fileNameSeparator}${docId}`;

        return this.fileStorage.storeCompressedData<ODataSPQModel>(storageId, docXml);
    }

    public loadQuestionnaireXmlFromCache(docId: string): Observable<ODataSPQModel> {
        const storageId = `${FormsConstants.KEY_FORMS}.SPQXml${this.fileNameSeparator}${docId}`;

        return this.fileStorage.loadCompressedData<ODataSPQModel>(storageId);
    }

    public removeQuestionnaireXmlFromCache(docId: string): Observable<void> {
        const storageId = `${FormsConstants.KEY_FORMS}.SPQXml${this.fileNameSeparator}${docId}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public removeLockedByFromFormXmlInCache(
        formId: FormIdentifier,
        notification: NotificationIdentifier,
    ): Observable<ODataGetXmlModel | void> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormXml${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.loadCompressedData<ODataGetXmlModel>(storageId).pipe(
            mergeMap((data) => {
                // remove the lockedBy
                data.LockedBy = '';

                return this.saveFormXmlToCache(formId, notification, data);
            }),
        );
    }

    public saveFormStateToCache(form: FormState): Observable<FormState> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormState${this.fileNameSeparator}${this.makeId(
            form.formId,
            form.notification,
        )}`;

        return this.loadFormSubmitStatus(form.notification).pipe(
            mergeMap((submitStatus) => {
                // Check if there is a submit status for this form
                const status = submitStatus[form.formId.formId];

                // if not existing or previously aborted, add the "EDITED" status for this form - but only if it was not opened in preview mode
                // if the form is already submitted or handover, Don`t update the form submitStatus to edited.
                const isStatusAllowed =
                    typeof status === 'undefined' ||
                    status === SUBMIT_STATUS.ABORTED ||
                    form.loadingStatus === FormsConstants.FORM_LOADING_STATUS.EDITED;
                const isReadOnlySubmitStatus = FormsConstants.readOnlySubmitStatuses.has(status);
                const hasReadOnlyReasonNone = form.readOnlyReason === FormsConstants.READONLY_REASON.NONE;
                const isSPQ = isServicePartQuestionnaire(form.type);

                if (isStatusAllowed && !isReadOnlySubmitStatus && hasReadOnlyReasonNone && !isSPQ) {
                    return this.saveFormSubmitStatus(
                        form.notification,
                        form.formId,
                        FormsConstants.SUBMIT_STATUS.EDITED,
                    );
                }

                return of(undefined);
            }),
            mergeMap(() => this.fileStorage.storeCompressedData(storageId, form)),
        );
    }

    public loadFormStateFromCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<FormState> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormState${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.loadCompressedData<FormState>(storageId);
    }

    public isFormStateInCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<boolean> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormState${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.checkCompressedData(storageId);
    }

    public removeFormStateFromCache(formId: FormIdentifier, notification: NotificationIdentifier): Observable<void> {
        const storageId = `${FormsConstants.KEY_FORMS}.FormState${this.fileNameSeparator}${this.makeId(
            formId,
            notification,
        )}`;

        return this.fileStorage.removeCompressedData(storageId);
    }

    public saveLastOpenedForm(lastOpenedForm: LastOpenedFormState): Observable<LastOpenedFormState> {
        const storageId = `${FormsConstants.KEY_FORMS}.LastOpenedForm`;

        return this.fileStorage.storeCompressedData(storageId, lastOpenedForm);
    }

    public loadLastOpenedForm(): Observable<LastOpenedFormState> {
        const storageId = `${FormsConstants.KEY_FORMS}.LastOpenedForm`;

        return this.fileStorage.loadCompressedData(storageId);
    }

    private makeId(formId: FormIdentifier, notification: NotificationIdentifier): string {
        const formString = FormUtilities.toString(formId, this.fileNameSeparator);
        const notificationString = NotificationUtilities.toString(notification, this.fileNameSeparator);

        return `${formString}${this.fileNameSeparator}${notificationString}`;
    }

    private makeSPQSubmitStatusStorageId(
        notification: NotificationIdentifier,
        form: FormIdentifier,
        materialKey: string,
    ): string {
        return `${FormsConstants.KEY_FORMS}.SPQSubmitStatus${this.fileNameSeparator}${this.makeId(form, notification)}${
            this.fileNameSeparator
        }${materialKey}`;
    }

    private makeSAFSubmitStatusStorageId(notification: NotificationIdentifier, form: FormIdentifier): string {
        return `${FormsConstants.KEY_FORMS}.SAFSubmitStatus${this.fileNameSeparator}${this.makeId(form, notification)}`;
    }
}
